/**
 * Helper per conversione flessibile da vari tipi a DateTime
 * Replica il comportamento permissivo di moment()
 */

import { DateTime } from 'luxon';

/**
 * Converte vari tipi di input in DateTime (come moment() faceva)
 * 
 * Supporta:
 * - Date
 * - string (ISO)
 * - number (millisecondi Unix)
 * - DateTime (passthrough)
 * - null/undefined (ritorna DateTime.invalid)
 */
export function toDateTime(value: any, format?: string, options?: { zone?: string }): DateTime {
    if (value == null) {
        return DateTime.invalid('null or undefined value');
    }

    // Se è già un DateTime, ritornalo
    if (value instanceof DateTime) {
        return value;
    }

    // Se è una Date JavaScript
    if (value instanceof Date) {
        return DateTime.fromJSDate(value, options);
    }

    // Se è un numero (millisecondi Unix)
    if (typeof value === 'number') {
        return DateTime.fromMillis(value, options);
    }

    // Se è una stringa
    if (typeof value === 'string') {
        // Se ha un formato specifico
        if (format) {
            return DateTime.fromFormat(value, format, options);
        }

        // Altrimenti prova ISO
        const dt = DateTime.fromISO(value, options);
        if (dt.isValid) {
            return dt;
        }

        // Se ISO fallisce, prova SQL
        const dtSQL = DateTime.fromSQL(value, options);
        if (dtSQL.isValid) {
            return dtSQL;
        }

        // Ultima chance: prova a parsare come Date
        const dateObj = new Date(value);
        if (!isNaN(dateObj.getTime())) {
            return DateTime.fromJSDate(dateObj, options);
        }
    }

    // Se arriviamo qui, non siamo riusciti a parsare
    return DateTime.invalid(`Unable to parse value: ${value}`);
}

/**
 * Shortcut per conversione veloce da qualsiasi cosa a DateTime
 * Equivalente a: moment(value)
 */
export function dt(value: any): DateTime {
    return toDateTime(value);
}

/**
 * Shortcut per formato + conversione
 * Equivalente a: moment(value, format)
 */
export function dtFormat(value: string, format: string, zone?: string): DateTime {
    return toDateTime(value, format, { zone });
}

